import { useEffect, useState } from "react";
import { AlertCircle, Clock, Plus, Pencil, Trash2, ArrowRight, ArrowLeft, ScrollText,
  Search, SlidersHorizontal, Settings as SettingsIcon, CalendarCheck, Grid3x3, Check, AlertTriangle, ShieldCheck, Box, Download, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SYSTEM_CATEGORIES = [
  "Vernier Caliper", "Micrometer", "Height Gauge", "Bore Gauge", "Depth Gauge", "Radius Gauge",
  "Protractor", "Dial Indicator", "Pin Gauge", "Thread Gauge", "Surface Roughness Tester",
  "CMM", "Profile Projector", "Feeler Gauge",
];

const STATUS = {
  up_to_date: { label: "Up to date", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  due_soon: { label: "Due soon", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50 border-amber-200" },
  overdue: { label: "Overdue", dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50 border-red-200" },
  not_set: { label: "Not set", dot: "bg-slate-400", text: "text-slate-600", bg: "bg-slate-50 border-slate-200" },
};
const BLANK = { name: "", code: "", category: "", location: "", company: "", last_cal: "", interval_days: "", notes: "" };

export default function GaugePanel() {
  const [view, setView] = useState("dashboard");   // dashboard | list
  const [stats, setStats] = useState(null);
  const [gauges, setGauges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);          // null | {…} (add/edit modal)
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("categories");

  function exportCsv() {
    const cols = ["name", "code", "category", "location", "company", "last_cal", "interval_days", "next_due", "status"];
    const rows = [cols.join(",")].concat(gauges.map((g) => cols.map((c) => `"${String(g[c] ?? "").replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "gauges.csv"; a.click();
  }

  async function load() {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([api.get("/gauges/stats"), api.get("/gauges")]);
      setStats(s.data); setGauges(g.data.gauges || []);
    } catch { setStats(null); setGauges([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function saveGauge() {
    if (!form.name.trim()) { toast.error("Gauge name is required"); return; }
    setSaving(true);
    try {
      const body = { ...form, interval_days: form.interval_days ? parseInt(form.interval_days) : 0 };
      if (form.id) await api.put(`/gauges/${form.id}`, body);
      else await api.post("/gauges", body);
      toast.success(form.id ? "Gauge updated" : "Gauge added");
      setForm(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }
  async function removeGauge(g) {
    if (!window.confirm(`Delete gauge "${g.name}"?`)) return;
    try { await api.delete(`/gauges/${g.id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  const s = stats || { total: 0, up_to_date: 0, due_soon: 0, overdue: 0, not_set: 0, overdue: 0, due_within_30: 0, locations: 0, companies: 0, categories: 0 };

  if (loading) return <div className="text-center py-20 text-sm text-slate-400">Loading gauges…</div>;

  // ---- List view (View all gauges) — pre6-style toolbar + cards ----
  if (view === "list") {
    const ql = query.trim().toLowerCase();
    const shown = gauges.filter((g) => !ql || `${g.name} ${g.code} ${g.category} ${g.location} ${g.company}`.toLowerCase().includes(ql));
    const compliance = s.total ? Math.round(((s.total - s.overdue) / s.total) * 100) : 100;
    return (
      <div className="max-w-5xl mx-auto">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap mb-6">
          <button onClick={() => setView("dashboard")} className="text-slate-500 hover:text-slate-800" title="Back to dashboard"><ArrowLeft size={18} /></button>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Add gauges to search…" className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm" />
          </div>
          <button className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"><SlidersHorizontal size={14} /> Filters</button>
          <div className="flex items-center gap-1.5">
            <Chip icon={<Grid3x3 size={13} />} n={s.total} cls="bg-slate-100 text-slate-600" />
            <Chip icon={<Check size={13} />} n={s.up_to_date} cls="bg-emerald-50 text-emerald-700" />
            <Chip icon={<Clock size={13} />} n={s.due_soon} cls="bg-amber-50 text-amber-700" />
            <Chip icon={<AlertTriangle size={13} />} n={s.overdue} cls="bg-red-50 text-red-700" />
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 px-1.5"><ShieldCheck size={14} /> {compliance}%</span>
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <button onClick={() => setSettingsOpen(true)} className="p-2 border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50" title="Settings"><SettingsIcon size={16} /></button>
          <button onClick={() => toast("Select gauges to calibrate")} className="inline-flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"><CalendarCheck size={15} /> Calibrate</button>
          <button onClick={() => setForm({ ...BLANK })} className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-md px-3 py-2"><Plus size={15} /> Add Gauge</button>
        </div>

        {shown.length === 0 ? (
          <div className="text-center py-24">
            <Box size={54} className="mx-auto text-slate-300 mb-3" strokeWidth={1.2} />
            <div className="text-lg font-semibold text-slate-800">No gauges yet</div>
            <div className="text-sm text-slate-500 mt-1">{ql ? "No gauges match your search." : "Add a gauge to get started."}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {shown.map((g) => {
              const st = STATUS[g.status] || STATUS.not_set;
              return (
                <div key={g.id} className={`border rounded-xl bg-white p-3 ${st.bg}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{g.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{g.code || "—"}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${st.text}`}><span className={`w-2 h-2 rounded-full ${st.dot}`} />{st.label}</span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                    {g.category && <div>Category: <span className="text-slate-700">{g.category}</span></div>}
                    {g.location && <div>Location: <span className="text-slate-700">{g.location}</span></div>}
                    <div>Next due: <span className="text-slate-700">{g.next_due || "—"}</span></div>
                  </div>
                  <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-slate-100">
                    <button onClick={() => setForm({ ...g, interval_days: g.interval_days || "", last_cal: (g.last_cal || "").slice(0, 10) })} className="p-1 text-slate-400 hover:text-slate-700"><Pencil size={14} /></button>
                    <button onClick={() => removeGauge(g)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {form && <GaugeForm form={form} setForm={setForm} onSave={saveGauge} saving={saving} />}
        {settingsOpen && <GaugeSettings gauges={gauges} tab={settingsTab} setTab={setSettingsTab} onClose={() => setSettingsOpen(false)} onExport={exportCsv} />}
      </div>
    );
  }

  // ---- Dashboard view ----
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-center gap-8 text-slate-700 mb-4">
        <div className="inline-flex items-center gap-2"><AlertCircle size={17} className="text-red-500" /><b>{s.overdue}</b> overdue</div>
        <div className="inline-flex items-center gap-2"><Clock size={17} className="text-amber-500" /><b>{s.due_within_30}</b> due within 30 days</div>
      </div>

      <div className="border border-slate-200 rounded-xl bg-white p-4 mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Compliant</div>
        <div className="flex items-center gap-6 flex-wrap mt-1">
          <div className="text-slate-800"><b className="text-lg">{s.total}</b> gauges</div>
          <Legend dot="bg-emerald-500" label="Up to date" n={s.up_to_date} />
          <Legend dot="bg-amber-500" label="Due soon" n={s.due_soon} />
          <Legend dot="bg-red-500" label="Overdue" n={s.overdue} />
          <Legend dot="bg-slate-400" label="Not set" n={s.not_set} />
        </div>
        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden flex">
          {["up_to_date", "due_soon", "overdue", "not_set"].map((k) => {
            const pct = s.total ? (s[k] / s.total) * 100 : 0;
            const col = { up_to_date: "bg-emerald-500", due_soon: "bg-amber-500", overdue: "bg-red-500", not_set: "bg-slate-300" }[k];
            return pct > 0 ? <div key={k} className={col} style={{ width: `${pct}%` }} /> : null;
          })}
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate-800">Recent activity</div>
        <button onClick={() => toast("Audit log — coming soon")} className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md px-3 py-1.5"><ScrollText size={14} /> Audit log</button>
      </div>
      <div className="text-center text-sm text-slate-400 py-8">No recent activity.</div>

      <div className="flex justify-center my-4">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-5 py-2.5">View all gauges <ArrowRight size={15} /></button>
      </div>

      <div className="border-t border-slate-200 pt-3 flex items-center gap-4 flex-wrap text-sm text-slate-600">
        <span><b>{s.total}</b> / 300 gauges</span>
        <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-slate-300" style={{ width: `${Math.min(100, (s.total / 300) * 100)}%` }} /></div>
        <span className="text-slate-400">Complimentary</span>
        <span><b>{s.locations}</b> Locations</span>
        <span><b>{s.companies}</b> Companies</span>
        <span><b>{s.categories}</b> Categories</span>
        <button onClick={() => toast("Upgrade — contact admin")} className="ml-auto border border-slate-300 rounded-md px-3 py-1 text-sm hover:bg-slate-50">Upgrade</button>
      </div>

      {form && <GaugeForm form={form} setForm={setForm} onSave={saveGauge} saving={saving} />}
    </div>
  );
}

function Legend({ dot, label, n }) {
  return <span className="inline-flex items-center gap-1.5 text-sm text-slate-600"><span className={`w-2.5 h-2.5 rounded-full ${dot}`} />{label} {n}</span>;
}

function Chip({ icon, n, cls }) {
  return <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-md px-2 py-1 ${cls}`}>{icon} {n}</span>;
}

function GaugeSettings({ gauges, tab, setTab, onClose, onExport }) {
  const [q, setQ] = useState("");
  const distinct = (f) => [...new Set(gauges.map((g) => (g[f] || "").trim()).filter(Boolean))].sort();
  const NAV = [
    { grp: "Workspace", items: [["categories", "Categories"], ["locations", "Locations"], ["companies", "Companies"]] },
    { grp: "Organization", items: [["team", "Team"], ["plans", "Plans"], ["notifications", "Notifications"]] },
  ];
  const usedCats = distinct("category");
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-14 bg-black/30 p-4" onClick={onClose}>
      <div className="w-[760px] max-w-full max-h-[82vh] bg-white rounded-xl shadow-2xl flex overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Left nav */}
        <div className="w-52 shrink-0 border-r border-slate-100 p-3 flex flex-col">
          <div className="font-semibold text-slate-800 px-2 mb-2">Settings</div>
          {NAV.map((sec) => (
            <div key={sec.grp} className="mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 px-2 mb-1">{sec.grp}</div>
              {sec.items.map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} className={`w-full text-left px-2 py-1.5 rounded-md text-sm ${tab === id ? "bg-slate-100 font-semibold text-slate-800" : "text-slate-600 hover:bg-slate-50"}`}>{label}</button>
              ))}
            </div>
          ))}
          <div className="mt-auto pt-2 border-t border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 px-2 mb-1">Tools</div>
            <button onClick={onExport} className="w-full text-left px-2 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2"><Download size={14} /> Export gauges</button>
          </div>
        </div>
        {/* Panel */}
        <div className="flex-1 min-w-0 p-5 overflow-y-auto">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-lg font-semibold text-slate-800 capitalize">{tab}</div>
              <div className="text-sm text-slate-500">{{
                categories: "Organize gauges into system and custom categories.",
                locations: "Where your gauges are stored or used.",
                companies: "Owning / supplier companies for your gauges.",
              }[tab] || "Coming soon."}</div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>

          {tab === "categories" && (
            <div className="mt-4">
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories by name or code…" className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm" />
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">System categories ({SYSTEM_CATEGORIES.length})</div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {SYSTEM_CATEGORIES.filter((c) => !q || c.toLowerCase().includes(q.toLowerCase())).map((c) => (
                  <span key={c} className="text-xs border border-slate-200 bg-slate-50 rounded-full px-2.5 py-1 text-slate-600">{c}</span>
                ))}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Custom categories</div>
              {usedCats.filter((c) => !SYSTEM_CATEGORIES.includes(c)).length === 0
                ? <div className="text-sm text-slate-400 italic">No custom categories yet.</div>
                : <div className="flex flex-wrap gap-1.5">{usedCats.filter((c) => !SYSTEM_CATEGORIES.includes(c)).map((c) => <span key={c} className="text-xs border border-orange-200 bg-orange-50 rounded-full px-2.5 py-1 text-orange-700">{c}</span>)}</div>}
            </div>
          )}
          {(tab === "locations" || tab === "companies") && (
            <div className="mt-4">
              {distinct(tab === "locations" ? "location" : "company").length === 0
                ? <div className="text-sm text-slate-400 italic">No {tab} yet — set them when adding a gauge.</div>
                : <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">{distinct(tab === "locations" ? "location" : "company").map((v) => <li key={v} className="px-3 py-2 text-sm text-slate-700">{v}</li>)}</ul>}
            </div>
          )}
          {["team", "plans", "notifications"].includes(tab) && (
            <div className="mt-8 text-center text-sm text-slate-400">{tab[0].toUpperCase() + tab.slice(1)} — coming soon.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GaugeForm({ form, setForm, onSave, saving }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setForm(null)}>
      <div className="w-[460px] max-h-[88vh] overflow-y-auto bg-white rounded-xl shadow-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-semibold text-slate-800 mb-3">{form.id ? "Edit gauge" : "Add gauge"}</div>
        <div className="space-y-3">
          <Field label="Gauge name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code / ID"><input value={form.code} onChange={(e) => set("code", e.target.value)} className={inp} /></Field>
            <Field label="Category"><input value={form.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Vernier" className={inp} /></Field>
            <Field label="Location"><input value={form.location} onChange={(e) => set("location", e.target.value)} className={inp} /></Field>
            <Field label="Company"><input value={form.company} onChange={(e) => set("company", e.target.value)} className={inp} /></Field>
            <Field label="Last calibration"><input type="date" value={form.last_cal} onChange={(e) => set("last_cal", e.target.value)} className={inp} /></Field>
            <Field label="Interval (days)"><input type="number" value={form.interval_days} onChange={(e) => set("interval_days", e.target.value)} placeholder="365" className={inp} /></Field>
          </div>
          <Field label="Notes"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={`${inp} resize-none`} /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={() => setForm(null)} className="px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 text-sm font-semibold bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white rounded-md">{saving ? "Saving…" : "Save gauge"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs text-slate-500">{label}</span><div className="mt-0.5">{children}</div></label>;
}
